import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, Row, Col, Card, CardBody } from "reactstrap";
import { useForm } from "react-hook-form";
import { ChevronLeft, ChevronRight, Package, Upload, MapPin, User, Truck, Download, FileUp, CheckCircle, AlertCircle } from "lucide-react";
import { createPackage, createBulkPackages } from "../../api/packages";
import { getRegions } from "../../api/contracts";
import { useCountry } from "../../context/CountryContext";
import { getCommerceCapabilities } from "../../api/settings";
import { showToast } from "../../utils/showToast";
import { parseCodAmount, COD_AMOUNT_RULE } from "../../utils/codAmount";
import { resolveCoverage } from "../../utils/coverageCommunes";
import { describeCreatePackageError, describeBulkRejection } from "../../utils/createPackageErrors";
import useAuth from "../../hooks/useAuth";
import { toast } from "../../utils/toast";
import MobileFirstPickupStep from "./MobileFirstPickupStep";
import MobileFirstProcessingStep from "./MobileFirstProcessingStep";
// Eliminado: MethodSelectionStep. Renderizamos inline las cards del método
// Eliminado: PickupAddressStep (unificamos con MobileFirstPickupStep)
import RecipientStep from "./RecipientStep";
import PackageDetailsStep from "./PackageDetailsStep";
import ExcelUploadStep from "./ExcelUploadStep";
import "./createPackageWizard.scss";

export default function CreatePackageWizard({
  closeModal,
  fetch,
  setConfirmationModal,
  setPackagesCreated,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState({});
  const [loading, setLoading] = useState(false);
  // Último rechazo del alta manual: { title, details, fieldErrors, targetStep }.
  // Se muestra como aviso persistente (un toast desaparece antes de poder leer
  // qué dato corregir) hasta el siguiente intento de avanzar.
  const [submitError, setSubmitError] = useState(null);
  // Último rechazo del lote de Excel: { title, details }. Persistente por el
  // mismo motivo que `submitError` (un toast de 6 s no alcanza para leer qué
  // fila corregir), pero separado porque vuelve al paso 1 y no marca campos del
  // formulario. Se limpia al reintentar, al elegir otro archivo y al cambiar de
  // método.
  const [bulkError, setBulkError] = useState(null);
  const { t } = useTranslation();
  const { user, userData } = useAuth();
  // La marca sale de `userData` (GET /users/:id) y NO del token: el JWT no lleva
  // isSalesSupervisor, así que leerlo de `user` daría siempre undefined. Mismo
  // patrón que `userData.isSeller` en Mi Cuenta.
  const isSalesSupervisor = userData?.isSalesSupervisor === true;
  // Compatibilidad: algunas sesiones traen shippingAddresses en user.companyId
  const companyShippingAddresses = user?.company?.shippingAddresses 
    || user?.companyId?.shippingAddresses 
    || [];
  
  const methods = useForm({ mode: "onChange" });

  const [regionOptions, setRegionOptions] = useState([]);
  const { countryId, countryName, labels: countryLabels, loading: countryLoading } = useCountry();

  // Pago contra entrega: el comercio no puede leer la config de su operador, se la
  // informa el BFF. Arranca apagado a propósito (fail-closed): si el endpoint no
  // responde preferimos no ofrecer el campo antes que generar pedidos con un monto
  // que el operador no va a cobrar.
  const [cod, setCod] = useState({ enabled: false, currency: "" });

  // Estados para flujo de Excel
  const [fileName, setFileName] = useState();
  const [fileData, setFileData] = useState();
  const [packagesJSON, setPackagesJSON] = useState();
  const [selectedAddress, setSelectedAddress] = useState(null);

  const selectedMethod = wizardData.method;

  const handleMethodSelect = (method) => {
    setWizardData((previous) => ({ ...previous, method }));
    setBulkError(null);
    setCurrentStep(1);
  };

  // Cargar regiones para poder mapear el código a nombre completo
  useEffect(() => {
    // Guard: sin el país del comercio resuelto no hay catálogo que pedir.
    if (countryLoading || !countryId) return;
    const loadRegions = async () => {
      try {
        const data = await getRegions({ countryId });
        if (!data?.error) {
          setRegionOptions(data.map(r => ({ value: r.code, label: r.name })));
        }
      } catch (e) {
        // silencioso
      }
    };
    loadRegions();
  }, [countryId, countryLoading]);

  // Capacidades que el operador habilita para este comercio. Una sola lectura al abrir
  // el wizard: la usan tanto el paso "Paquete" como la carga masiva.
  useEffect(() => {
    const loadCapabilities = async () => {
      try {
        const response = await getCommerceCapabilities();
        // El contrato es { cashOnDelivery: … } pelado, pero varios proxies del BFF
        // envuelven en { data: … }. Toleramos ambas formas: leer mal el envase
        // apagaría el cobro sin ningún error visible (mismo criterio que CodSection).
        const body = response?.data;
        const capabilities = body?.data ?? body;
        const cashOnDelivery = capabilities?.cashOnDelivery;
        // `null` = el backend no pudo determinarlo. Se trata igual que apagado.
        if (cashOnDelivery?.enabled) {
          setCod({
            enabled: true,
            currency: cashOnDelivery.currency || "",
          });
        }
      } catch (e) {
        // silencioso: el wizard sigue operativo, sólo sin cobro contra entrega
      }
    };
    loadCapabilities();
  }, []);

  // Cobertura real del operador que atiende a este comercio. Sirve para dos
  // cosas en la carga masiva: validar la comuna de cada fila y entregar la
  // plantilla con SU lista de comunas en el desplegable.
  //
  // Arranca en `null` y se queda en `null` si algo falla. Los dos caminos del
  // wizard leen ese `null` distinto, y es deliberado:
  //
  //  - Pedido a mano: no se valida la comuna y listo. La región la elige el
  //    usuario en un desplegable, así que no se inventa ningún dato.
  //  - Carga por Excel: se BLOQUEA. Ahí la región se deriva de la comuna de
  //    cada fila, y sin cobertura habría que inventarla. Un pedido con la
  //    región equivocada no se nota al cargarlo: se nota tres pasos después.
  const [coverage, setCoverage] = useState(null);
  // "cargando", "no se pudo resolver" y "no hay cobertura configurada" son
  // estados distintos, y confundirlos daría el aviso equivocado: bloqueados
  // están los dos últimos, pero el tercero es permanente (la empresa no tiene
  // contrato activo o el operador nunca declaró cobertura) y no se arregla
  // reintentando, así que no puede decirle al comercio que vuelva a intentar.
  // `coverageStatus` viaja a MobileFirstFileInput por ExcelUploadStep.
  const [coverageStatus, setCoverageStatus] = useState("loading");
  // Texto del backend cuando la cobertura falló con un motivo (p. ej. un 403
  // explicado); null si sólo hubo un error genérico.
  const [coverageError, setCoverageError] = useState(null);
  useEffect(() => {
    if (countryLoading) return undefined;
    let cancelled = false;
    setCoverageStatus("loading");
    setCoverageError(null);
    resolveCoverage(countryId).then((result) => {
      if (cancelled) return;
      if (result.reason === "ok") {
        setCoverage(result.coverage);
        setCoverageStatus("ready");
        return;
      }
      setCoverage(null);
      setCoverageError(result.message || null);
      setCoverageStatus(
        result.reason === "no-coverage" || result.reason === "unresolvable"
          ? result.reason
          : "failed",
      );
    });
    return () => { cancelled = true; };
  }, [countryId, countryLoading]);

  // Definir pasos según el método seleccionado
  const getSinglePackageSteps = () => [
    { id: 'method', title: 'Elige', subtitle: 'Crea o sube los datos', icon: Package },
    { id: 'pickup', title: 'Retiro', subtitle: 'Dirección de colecta de tus envíos', icon: MapPin },
    { id: 'recipient', title: 'Destinatario', subtitle: 'Datos del receptor', icon: User },
    { id: 'package', title: 'Paquete', subtitle: 'Detalles del envío', icon: Truck },
    { id: 'processing', title: 'Procesando', subtitle: 'Creando paquete', icon: Download }
  ];

  const getExcelSteps = () => [
    { id: 'method', title: 'Elige', subtitle: 'Crea o sube los datos', icon: Package },
    { id: 'upload', title: 'Cargar Excel', subtitle: 'Sube tu archivo', icon: FileUp },
    { id: 'pickup-excel', title: 'Retiro', subtitle: 'Dirección de colecta de tus envíos', icon: MapPin },
    { id: 'processing-excel', title: 'Procesando', subtitle: 'Validando y creando', icon: Download }
  ];

  const steps = selectedMethod === 'single' ? getSinglePackageSteps() : 
                selectedMethod === 'bulk' ? getExcelSteps() : 
                [{ id: 'method', title: 'Elige', subtitle: 'Crea o sube los datos', icon: Package }];

  const currentStepData = steps[currentStep];

  // Siempre mostrar el paso de dirección de retiro
  const hasMultipleAddresses = Array.isArray(companyShippingAddresses) && companyShippingAddresses.length > 1;


  const handleNext = useCallback(async () => {
    // Delay más largo para asegurar que el estado se haya actualizado completamente
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Validar el paso actual antes de avanzar usando una función inline
    let canAdvance = false;
    
    if (selectedMethod === 'single') {
      switch (currentStep) {
        case 0: // Método
          canAdvance = !!wizardData.method;
          if (!canAdvance) {
            showToast(t("Por favor selecciona un método de creación"), false);
          }
          break;
        case 1: // Retiro
          canAdvance = !!(selectedAddress?.value || wizardData.pickupAddress);
          if (!canAdvance) {
            showToast(t("Por favor selecciona una dirección de retiro"), false);
          }
          break;
        case 2: // Destinatario
          canAdvance = !!(methods.getValues &&
            methods.getValues("receiver_name") &&
            methods.getValues("receiver_phone") &&
            methods.getValues("regionCode") &&
            methods.getValues("municipality") &&
            methods.getValues("address_line"));
          if (!canAdvance) {
            showToast(t("Por favor completa los datos del destinatario"), false);
          }
          break;
        case 3: { // Paquete (opcional)
          // Todos los campos de "Detalles del envío" son opcionales.
          // Siempre permitimos avanzar, independiente de si se llenó 1, ninguno o todos los campos.
          // Única excepción: un monto a cobrar mal escrito. Dejarlo pasar significaría
          // descartarlo en silencio al armar el payload y crear el pedido sin cobro.
          const codRaw = methods.getValues ? methods.getValues("cod_amount") : undefined;
          const codParsed = parseCodAmount(codRaw);
          canAdvance = !(cod.enabled && codParsed.status === "invalid");
          if (!canAdvance) {
            showToast(`${t("Monto a cobrar inválido.")} ${t(COD_AMOUNT_RULE)}`, false);
          }
          break;
        }
        case 4: // Procesar
          canAdvance = true;
          break;
        default:
          canAdvance = false;
      }
    } else if (selectedMethod === 'bulk') {
      switch (currentStep) {
        case 0: // Método
          canAdvance = !!wizardData.method;
          break;
        case 1: // Cargar Excel
          console.log("Validando paso 1 inline:", { 
            fileData: !!fileData, 
            packagesJSON: !!packagesJSON,
            fileName: fileName,
            fileDataType: typeof fileData,
            packagesJSONType: typeof packagesJSON,
            packagesJSONLength: packagesJSON?.length,
            packagesJSONContent: packagesJSON,
            timestamp: new Date().toISOString(),
            currentStep: currentStep,
            selectedMethod: selectedMethod
          });
          
          // Verificar que fileData exista
          if (!fileData) {
            console.log("Validación falló - no hay fileData");
            showToast(t("Por favor carga un archivo Excel válido"), false);
            canAdvance = false;
            break;
          }
          
          // Verificar que packagesJSON exista y sea un array válido
          if (!packagesJSON) {
            console.log("Validación falló - no hay packagesJSON");
            showToast(t("Por favor carga un archivo Excel válido"), false);
            canAdvance = false;
            break;
          }
          
          if (!Array.isArray(packagesJSON)) {
            console.log("Validación falló - packagesJSON no es un array:", typeof packagesJSON);
            showToast(t("Por favor carga un archivo Excel válido"), false);
            canAdvance = false;
            break;
          }
          
          if (packagesJSON.length === 0) {
            console.log("Validación falló - packagesJSON está vacío");
            showToast(t("Por favor carga un archivo Excel válido"), false);
            canAdvance = false;
            break;
          }
          
          console.log("Validación exitosa - permitiendo avanzar");
          canAdvance = true;
          break;
        case 2: // Dirección de retiro
          console.log("Validando paso 2 inline:", { 
            selectedAddress: !!selectedAddress,
            selectedAddressValue: selectedAddress?.value,
            selectedAddressLabel: selectedAddress?.label,
            addressesLength: companyShippingAddresses.length
          });
          
          // Si solo hay una dirección, auto-seleccionarla automáticamente
          if (companyShippingAddresses.length === 1) {
            console.log("Auto-seleccionando dirección automáticamente");
            const addressData = {
              value: companyShippingAddresses[0],
              label: `${companyShippingAddresses[0].address}, ${companyShippingAddresses[0].municipality}, ${companyShippingAddresses[0].state}`.toUpperCase()
            };
            setSelectedAddress(addressData);
            
            // Esperar a que el estado se actualice
            await new Promise(resolve => setTimeout(resolve, 300));
            console.log("Dirección auto-seleccionada:", addressData);
            
            // Retornar true directamente ya que sabemos que hay una dirección
            console.log("Validación exitosa - dirección auto-seleccionada");
            canAdvance = true;
            break;
          }
          
          if (!selectedAddress) {
            console.log("Validación falló - no hay dirección seleccionada");
            showToast(t("Por favor selecciona una dirección de retiro"), false);
            canAdvance = false;
            break;
          }
          console.log("Validación exitosa - dirección seleccionada");
          canAdvance = true;
          break;
        case 3: // Procesar
          canAdvance = true;
          break;
        default:
          canAdvance = false;
      }
    }
    
    if (!canAdvance) {
      return;
    }

    setSubmitError(null);
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, steps.length, selectedMethod, fileData, packagesJSON, fileName, selectedAddress, companyShippingAddresses, wizardData, cod]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const isStepCompleted = (stepIndex) => {
    if (selectedMethod === 'single') {
      if (stepIndex === 0) return !!wizardData.method;
      if (stepIndex === 1) return !!(selectedAddress || wizardData.pickupAddress);
      if (stepIndex === 2) {
        // Verificar que todos los campos requeridos del destinatario estén completos
        const formValues = methods && methods.getValues ? methods.getValues() : {};
        return !!(
          formValues.receiver_name &&
          formValues.receiver_phone &&
          formValues.regionCode &&
          formValues.municipality &&
          formValues.address_line
        );
      }
      if (stepIndex === 3) return !!wizardData.package;
      if (stepIndex === 4) return wizardData.completed;
    } else if (selectedMethod === 'bulk') {
      if (stepIndex === 0) return !!wizardData.method;
      if (stepIndex === 1) return !!fileData && !!packagesJSON;
      if (stepIndex === 2) return !!selectedAddress;
      if (stepIndex === 3) return wizardData.completed;
    }
    return false;
  };

  const isStepActive = (stepIndex) => stepIndex === currentStep;

  // Auto-seleccionar dirección si solo hay una (flujo single)
  useEffect(() => {
    if (selectedMethod === 'single' && currentStep === 1 && companyShippingAddresses.length === 1 && !selectedAddress) {
      const auto = { value: companyShippingAddresses[0], label: `${companyShippingAddresses[0].address || companyShippingAddresses[0].street_name} ${companyShippingAddresses[0].street_number || ''}`.trim() };
      setSelectedAddress(auto);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedMethod]);

  // Monitorear cambios en el estado del archivo
  useEffect(() => {
    console.log("Wizard useEffect - Estado del archivo:", {
      fileData: !!fileData,
      packagesJSON: !!packagesJSON,
      fileName: fileName,
      currentStep: currentStep,
      selectedMethod: selectedMethod
    });
  }, [fileData, packagesJSON, fileName, currentStep, selectedMethod]);

  // Auto-seleccionar dirección si solo hay una (flujo Excel)
  useEffect(() => {
    console.log("Wizard useEffect - Auto-selección:", {
      selectedMethod,
      currentStep,
      addressesLength: companyShippingAddresses.length,
      selectedAddress: !!selectedAddress
    });
    
    if (selectedMethod === 'bulk' && currentStep === 2 && companyShippingAddresses.length === 1 && !selectedAddress) {
      console.log("Wizard auto-seleccionando dirección:", companyShippingAddresses[0]);
      const addressData = {
        value: companyShippingAddresses[0],
        label: `${companyShippingAddresses[0].address}, ${companyShippingAddresses[0].municipality}, ${companyShippingAddresses[0].state}`.toUpperCase()
      };
      setSelectedAddress(addressData);
      
      // Verificar que se haya establecido correctamente
      setTimeout(() => {
        console.log("Verificando auto-selección:", { selectedAddress: !!selectedAddress });
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedMethod]);

  // Procesar paquete único en el paso final
  useEffect(() => {
    if (selectedMethod === 'single' && currentStep === 4 && !wizardData.completed) {
      processSinglePackage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedMethod, wizardData.completed]);

  // Procesar paquetes desde Excel en el paso final
  useEffect(() => {
    if (selectedMethod === 'bulk' && currentStep === 3 && !wizardData.completed) {
      processBulkPackages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedMethod, wizardData.completed]);

  const processSinglePackage = async () => {
    setLoading(true);
    try {
      // Tomar los valores actuales del formulario
      const formValues = (methods && methods.getValues) ? methods.getValues() : {};

      // Construir payload congruente con createPackage.jsx
      // Obtener nombre completo de la región a partir del código
      const regionNameFull = regionOptions.find(r => r.value === formValues?.regionCode)?.label || "";

      const packageData = {
        type: "OTS",
        status: 0,
        order_id: formValues?.order_id || undefined,
        receiver_name: formValues?.receiver_name,
        receiver_lastName: formValues?.receiver_lastName || "",
        receiver_phone: formValues?.receiver_phone,
        comments: formValues?.comment || undefined,
        pickupAddress: wizardData?.pickupAddress,
        // Compatibilidad: algunas sesiones traen la empresa en user.companyId (igual que shippingAddresses arriba)
        companyId: user?.company?._id || user?.companyId?._id,
        packageDimensions: {
          height: formValues?.package_height ? parseFloat(formValues.package_height) : undefined,
          width: formValues?.package_width ? parseFloat(formValues.package_width) : undefined,
          length: formValues?.package_length ? parseFloat(formValues.package_length) : undefined,
          weight: formValues?.package_weight ? parseFloat(formValues.package_weight) : undefined,
        },
        shipping_address: {
          // Usamos street_name como línea de dirección principal
          street_name: formValues?.street_address || formValues?.address_line || "",
          street_number: undefined,
          city: { name: regionNameFull },
          municipality: { name: formValues?.municipality || formValues?.cityName || "" },
          // country es requerido por el DTO del backend (ShippingAddress.country en
          // microservice-packages). Sin este campo la validación devuelve 400 antes de
          // entrar a insertPackage. Los flujos webhook (Woo/Sendu) sí lo mandan.
          country: { name: countryName },
          comment: formValues?.comment || undefined,
        },
      };

      // Código postal declarado (solo se pide en países que lo usan). Se envía CRUDO
      // —trim + uppercase, sin derivar el CP de 4 dígitos— y se omite si viene vacío:
      // el backend distingue "sin CP" de "CP vacío" y no debe persistir cadenas vacías.
      const rawZip = String(formValues?.zip_code ?? "").trim().toUpperCase();
      if (rawZip) packageData.shipping_address.zip_code = rawZip;

      // Cobro contra entrega. El comercio declara SÓLO el monto: la moneda, el origen
      // y el estado inicial los fija el backend. La clave se omite por completo cuando
      // no hay monto válido — ni null ni 0 — porque su ausencia es lo que le dice al
      // backend que el pedido no lleva cobro.
      if (cod.enabled) {
        const codAmount = parseCodAmount(formValues?.cod_amount);
        if (codAmount.status === "ok") {
          packageData.cashOnDelivery = { amount: codAmount.amount };
        }
      }

      const response = await createPackage({ values: packageData });
      
      if (response.success && response.data) {
        setWizardData(prev => ({ ...prev, completed: true }));
        closeModal();
        if (setConfirmationModal) setConfirmationModal(true);
        // Asegurar que response.data sea un array para consistencia
        const packagesArray = Array.isArray(response.data) ? response.data : [response.data];
        if (setPackagesCreated) setPackagesCreated(packagesArray);
        if (fetch) fetch(true);
      } else {
        const described = describeCreatePackageError(response?.error, t, countryLabels);
        setSubmitError(described);
        setCurrentStep(described.targetStep);
      }
    } catch (error) {
      console.error("[Crear paquete] Error inesperado:", error);
      setSubmitError({
        title: t("No pudimos crear el pedido"),
        details: [t("Error al procesar la solicitud")],
        fieldErrors: [],
        targetStep: 3,
      });
      setCurrentStep(3);
    } finally {
      setLoading(false);
    }
  };

  const processBulkPackages = async () => {
    // Reintento: el aviso anterior ya no aplica hasta que este intento falle.
    setBulkError(null);
    setLoading(true);
    try {
      const addressData = selectedAddress.value;
      const { data, error } = await createBulkPackages({
        packages: packagesJSON,
        user: user,
        senderAddress: addressData.address,
        senderNeighborhood: addressData.municipality,
        senderState: addressData.state,
      });

      if (data) {
        setWizardData(prev => ({ ...prev, completed: true }));
        closeModal(false);
        setConfirmationModal(true);
        // Asegurar que data sea un array
        // data viene como { data: packagesArray } de createBulkPackages
        const packagesArray = Array.isArray(data) ? data : (data?.data || []);
        console.log('Paquetes creados recibidos:', packagesArray);
        console.log('Cantidad de paquetes:', packagesArray.length);
        console.log('Tipo de data:', typeof data, 'Es array?', Array.isArray(data));
        // Asegurar que siempre sea un array antes de guardarlo
        if (Array.isArray(packagesArray) && packagesArray.length > 0) {
          setPackagesCreated(packagesArray);
        } else {
          console.warn('No se recibieron paquetes válidos:', packagesArray);
          setPackagesCreated([]);
        }
        fetch(true);
      }

      if (error?.uncompletedCells) {
        setLoading(false);
        toast.info(
          `${t("Faltan por completar los siguientes campos:")} ${error.uncompletedCells.join(", ")}`,
          { autoClose: 10000 }
        );
        setCurrentStep(1); // Volver a cargar excel
        return;
      }

      if (error) {
        // Mostrar mensaje específico si es un error de order_id duplicado
        if (error.code === 'DUPLICATE_ORDER_ID' && error.order_id) {
          toast.error(
            t('Ya existe el número de venta "{{orderId}}". Por favor, corrija los datos e intente nuevamente.', { orderId: error.order_id }),
            { autoClose: 8000 }
          );
        } else {
          // El rechazo del lote se muestra como aviso PERSISTENTE, no como toast:
          // con 20 filas rechazadas, 6 segundos no alcanzan para leer ni la
          // primera. El título y las líneas los arma `describeBulkRejection`
          // (utils/createPackageErrors), que separa "no pudimos crear los
          // pedidos" de "no sabemos si se crearon": sin cuerpo del backend el
          // lote pudo quedar creado, y ahí reintentar a ciegas duplica pedidos.
          setBulkError(describeBulkRejection(error, t));
        }
        setCurrentStep(1);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Error al procesar la solicitud."));
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  };

  const onSelectDocument = (data) => {
    console.log("Wizard recibió datos:", data);
    const { file, packagesJSON } = data;
    // Archivo nuevo (o ninguno): el rechazo del lote anterior ya no corresponde.
    setBulkError(null);
    if (!file) {
      console.log("No hay archivo, limpiando estado");
      setFileName(null);
      setFileData(null);
      setPackagesJSON(null);
      return;
    }
    
    // Validar que packagesJSON sea un array válido
    if (!packagesJSON || !Array.isArray(packagesJSON) || packagesJSON.length === 0) {
      console.log("Error: packagesJSON no es válido:", packagesJSON);
      showToast(t("Error al procesar el archivo Excel"), false);
      return;
    }
    
    console.log("Actualizando estado del wizard...");
    
    // Usar callback para asegurar que el estado se actualice
    setFileName(file.name);
    setFileData(file);
    setPackagesJSON(packagesJSON);
    
    console.log("Wizard actualizado:", { 
      fileName: file.name, 
      fileData: !!file, 
      packagesJSON: !!packagesJSON,
      packagesCount: packagesJSON.length 
    });
    
    // Verificar el estado después de un delay más largo
    setTimeout(() => {
      console.log("Estado después del delay:", { fileData: !!file, packagesJSON: !!packagesJSON });
    }, 300);
    
    // Forzar re-render del componente para asegurar que el estado se actualice
    setTimeout(() => {
      console.log("Forzando re-render después de actualizar estado");
    }, 500);
  };

  // Marca en el formulario los campos que rechazó el backend. Va en un efecto (y no
  // junto al setCurrentStep) para aplicarse con el paso de destino ya montado. Una
  // sola vez por rechazo: si el usuario corrige el campo y navega, no debe reaparecer.
  // Antes de marcar, se limpian los campos del rechazo anterior: un reintento que falla
  // por otra causa no debe dejar en rojo un campo que el backend ya no objeta.
  const appliedSubmitErrorRef = useRef(null);
  const serverErrorFieldsRef = useRef([]);
  useEffect(() => {
    if (selectedMethod !== 'single' || !submitError) return;
    if (appliedSubmitErrorRef.current === submitError) return;
    appliedSubmitErrorRef.current = submitError;
    if (serverErrorFieldsRef.current.length) methods.clearErrors(serverErrorFieldsRef.current);
    serverErrorFieldsRef.current = submitError.fieldErrors.map(({ name }) => name);
    submitError.fieldErrors.forEach(({ name, message }) => {
      methods.setError(name, { type: "server", message }, { shouldFocus: false });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError, selectedMethod]);

  // Sincronizar dirección seleccionada con wizardData para el flujo 'single'
  useEffect(() => {
    if (selectedMethod === 'single' && selectedAddress?.value) {
      setWizardData(prev => ({ ...prev, pickupAddress: selectedAddress.value }));
    }
  }, [selectedMethod, selectedAddress, setWizardData]);

  const renderStepContent = () => {
    if (selectedMethod === 'single') {
      switch (currentStep) {
        case 0: {
          const methodOptions = [
            {
              id: 'single',
              title: 'Crear encomienda',
              subtitle: 'Agregar un paquete individual',
              icon: Package,
              description: 'Completa los datos para crear una nueva encomienda.'
            },
            {
              id: 'bulk',
              title: 'Cargar paquetes',
              subtitle: 'Importar múltiples paquetes desde Excel',
              icon: Upload,
              description: 'Sube un archivo Excel con tus envíos para procesarlos en lote.'
            }
          ];
          return (
            <div className="method-selection">
              <Row className="g-4">
                {methodOptions.map((method) => (
                  <Col md="6" key={method.id}>
                    <Card
                      className={`method-card ${wizardData.method === method.id ? 'selected' : ''}`}
                      onClick={() => handleMethodSelect(method.id)}
                    >
                      <CardBody className="text-center p-4">
                        <div className="method-card__icon">
                          <method.icon size={48} />
                        </div>
                        <h4 className="method-card__title">{t(method.title)}</h4>
                        <p className="method-card__subtitle">{t(method.subtitle)}</p>
                        <p className="method-card__description">{t(method.description)}</p>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          );
        }
        case 1:
          return (
            <MobileFirstPickupStep 
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
            addresses={companyShippingAddresses}
            hasMultipleAddresses={hasMultipleAddresses}
            />
          );
        case 2:
          return <RecipientStep methods={methods} coverage={coverage} coverageStatus={coverageStatus} />;
        case 3:
          return <PackageDetailsStep methods={methods} cod={cod} />;
        case 4:
          return <MobileFirstProcessingStep loading={loading} step="single" />;
        default:
          return null;
      }
    } else if (selectedMethod === 'bulk') {
      switch (currentStep) {
        case 0: {
          const methodOptions = [
            {
              id: 'single',
              title: 'Crear encomienda',
              subtitle: 'Agregar un paquete individual',
              icon: Package,
              description: 'Completa los datos para crear una nueva encomienda.'
            },
            {
              id: 'bulk',
              title: 'Cargar paquetes',
              subtitle: 'Importar múltiples paquetes desde Excel',
              icon: Upload,
              description: 'Sube un archivo Excel con tus envíos para procesarlos en lote.'
            }
          ];
          return (
            <div className="method-selection">
              <Row className="g-4">
                {methodOptions.map((method) => (
                  <Col md="6" key={method.id}>
                    <Card
                      className={`method-card ${wizardData.method === method.id ? 'selected' : ''}`}
                      onClick={() => handleMethodSelect(method.id)}
                    >
                      <CardBody className="text-center p-4">
                        <div className="method-card__icon">
                          <method.icon size={48} />
                        </div>
                        <h4 className="method-card__title">{t(method.title)}</h4>
                        <p className="method-card__subtitle">{t(method.subtitle)}</p>
                        <p className="method-card__description">{t(method.description)}</p>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          );
        }
        case 1:
          return <ExcelUploadStep
            fileName={fileName}
            onSelectDocument={onSelectDocument}
            cod={cod}
            isSalesSupervisor={isSalesSupervisor}
            coverage={coverage}
            coverageStatus={coverageStatus}
            coverageError={coverageError}
          />;
        case 2:
          return <MobileFirstPickupStep 
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            addresses={companyShippingAddresses}
            hasMultipleAddresses={hasMultipleAddresses}
          />;
        case 3:
          return <MobileFirstProcessingStep loading={loading} step="excel" />;
        default:
          return null;
      }
    } else {
      const methodOptions = [
        {
          id: 'single',
          title: 'Crear encomienda',
          subtitle: 'Agregar un paquete individual',
          icon: Package,
          description: 'Completa los datos para crear una nueva encomienda.'
        },
        {
          id: 'bulk',
          title: 'Cargar paquetes',
          subtitle: 'Importar múltiples paquetes desde Excel',
          icon: Upload,
          description: 'Sube un archivo Excel con tus envíos para procesarlos en lote.'
        }
      ];
      return (
        <div className="method-selection">
          <Row className="g-4">
            {methodOptions.map((method) => (
              <Col md="6" key={method.id}>
                <Card
                  className={`method-card ${wizardData.method === method.id ? 'selected' : ''}`}
                  onClick={() => handleMethodSelect(method.id)}
                >
                  <CardBody className="text-center p-4">
                    <div className="method-card__icon">
                      <method.icon size={48} />
                    </div>
                    <h4 className="method-card__title">{method.title}</h4>
                    <p className="method-card__subtitle">{method.subtitle}</p>
                    <p className="method-card__description">{method.description}</p>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      );
    }
  };

  const showNextButton = () => {
    if (!selectedMethod) return false;
    if (selectedMethod === 'single') {
      return currentStep < 4;
    } else if (selectedMethod === 'bulk') {
      return currentStep < 3;
    }
    return false;
  };

  return (
    <div className="package-wizard">
      {/* Header del Wizard */}
      <div className="package-wizard__header">
        <div className="package-wizard__title">
          <h2>{t("Crear pedido")}</h2>
          <p>{t("Sigue los pasos para crear tu encomienda de forma rápida y sencilla")}</p>
        </div>
      </div>

      {/* Indicador de pasos */}
      {selectedMethod && (
        <div className="package-wizard__steps">
          <div className="package-wizard__steps-container">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              const isCompleted = isStepCompleted(index);
              const isActive = isStepActive(index);
              // Sólo se puede volver a los pasos ya completados y anteriores al actual.
              // Hacia adelante no: cada paso valida lo suyo (archivo, dirección) y
              // saltarlo dejaría el pedido a medio armar.
              const canGoBack = isCompleted && index < currentStep;
              const goBackToStep = () => setCurrentStep(index);

              return (
                <div
                  key={step.id}
                  role={canGoBack ? 'button' : undefined}
                  tabIndex={canGoBack ? 0 : undefined}
                  onClick={canGoBack ? goBackToStep : undefined}
                  onKeyDown={canGoBack ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      goBackToStep();
                    }
                  } : undefined}
                  aria-current={isActive ? 'step' : undefined}
                  title={canGoBack ? t('Volver a {{step}}', { step: t(step.title) }) : undefined}
                  className={`package-wizard__step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${canGoBack ? 'package-wizard__step--back' : ''}`}
                >
                  <div className="package-wizard__step-icon">
                    {isCompleted ? (
                      <CheckCircle size={24} />
                    ) : (
                      <IconComponent size={24} />
                    )}
                  </div>
                  <div className="package-wizard__step-content">
                    <h3>{t(step.title)}</h3>
                    <p>{t(step.subtitle)}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="package-wizard__step-connector">
                      <div className="package-wizard__step-line" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contenido del paso actual */}
      <div className="package-wizard__content">
        {currentStepData && (
          <div className="package-wizard__content-header">
            <div className="package-wizard__content-icon">
              <currentStepData.icon size={32} />
            </div>
            <div className="package-wizard__content-info">
              <h3>{t(currentStepData.title)}</h3>
              <p>{t(currentStepData.subtitle)}</p>
            </div>
          </div>
        )}
        
        <div className={`package-wizard__content-body ${
          (selectedMethod === 'single' && currentStep === 1) || 
          (selectedMethod === 'bulk' && currentStep === 2)
            ? 'pickup-step' 
            : ''
        } ${
          (selectedMethod === 'single' && currentStep === 2) || 
          (selectedMethod === 'bulk' && currentStep === 3)
            ? 'recipient-step-container' 
            : ''
        }`}>
          {selectedMethod === 'single' && submitError && currentStep < 4 && (
            <div
              className="mb-3 rounded-lg border border-status-error/40 bg-status-error-soft p-3"
              role="alert"
              aria-live="assertive"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-status-error">
                <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>{submitError.title}</span>
              </div>
              <ul className="mt-1.5 mb-0 space-y-1 pl-6 text-xs text-status-error list-disc">
                {submitError.details.map((detail, index) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Mismo aviso persistente que el alta manual, para el rechazo del lote.
              Va en el paso de carga del Excel (a donde vuelve `processBulkPackages`)
              y nunca en el de procesamiento, que ya no tiene nada que corregir. */}
          {selectedMethod === 'bulk' && bulkError && currentStep < 3 && (
            <div
              className="mb-3 rounded-lg border border-status-error/40 bg-status-error-soft p-3"
              role="alert"
              aria-live="assertive"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-status-error">
                <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>{bulkError.title}</span>
              </div>
              <ul className="mt-1.5 mb-0 space-y-1 pl-6 text-xs text-status-error list-disc">
                {bulkError.details.map((detail, index) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>
          )}
          {renderStepContent()}
        </div>
      </div>

      {/* Navegación del wizard */}
      <div className="package-wizard__navigation">
        <div className="package-wizard__navigation-left">
          {currentStep > 0 && !loading && (
            <Button
              className="package-wizard__nav-button package-wizard__nav-button--secondary"
              onClick={handlePrevious}
            >
              <ChevronLeft size={18} />
              {t("Anterior")}
            </Button>
          )}
        </div>
        
        <div className="package-wizard__navigation-right">
          <Button
            className="package-wizard__nav-button package-wizard__nav-button--secondary"
            onClick={closeModal}
            disabled={loading}
          >
            {t("Cancelar")}
          </Button>
          {showNextButton() && (
            <Button
              className="package-wizard__nav-button package-wizard__nav-button--primary"
              onClick={handleNext}
              disabled={loading}
            >
              {t("Siguiente")}
              <ChevronRight size={18} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
